import { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { LICENSE } from "constants/RouteContants";
import { isNil, isObject } from "lodash";
import { getToken } from "selectors/UserSelectors";
import store from "store";
import {
  clearSupportAccessSession,
  getSupportAccessToken,
  hasStoredSupportAccessSession,
  isSupportAccessActive,
  USE_ACTOR_TOKEN_HEADER,
} from "utils/SupportAccessUtils";

export const requestInterceptor = (config: AxiosRequestConfig) => {
  const headers = (config.headers || {}) as Record<string, any>;
  const useActorToken = Boolean(headers[USE_ACTOR_TOKEN_HEADER]);
  const hadSupportAccessSession = hasStoredSupportAccessSession();
  const supportAccessToken = useActorToken ? "" : getSupportAccessToken();

  delete headers[USE_ACTOR_TOKEN_HEADER];

  if (!useActorToken && hadSupportAccessSession && !supportAccessToken) {
    clearSupportAccessSession();
    redirectAfterSupportSessionEnd();
    return Promise.reject({
      responseMeta: {
        status: 401,
        message: "Support access session expired.",
      },
    });
  }

  const token = useActorToken
    ? getToken(store.getState())
    : supportAccessToken || getToken(store.getState());

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  config.headers = headers;

  return config;
};

export const responseSuccessInterceptor = (res: AxiosResponse) => {
  return res.data;
};

export const responseFailureInterceptor = (res: AxiosError) => {
  if (res.response?.status === 401) {
    if (isSupportAccessActive()) {
      clearSupportAccessSession();
      redirectAfterSupportSessionEnd();
      return Promise.reject({
        responseMeta: {
          status: res.response.status,
          message: "Support access session expired.",
        },
      });
    }

    return Promise.reject({
      responseMeta: {
        status: res.response.status,
        message: res.message,
      },
    });
  }

  return Promise.reject(res.response?.data);
};

// Full reload on a whitelisted route: reloading /support-access directly
// cannot complete the Auth0 redirect (the route is not an allowed callback
// URL), leaving the app stuck on the auth loader. Every session-end path must
// reload so customer-scoped Redux state never outlives the delegated session;
// only skip when already on the landing route to avoid a reload loop.
function redirectAfterSupportSessionEnd() {
  if (typeof window !== "undefined" && window.location.pathname !== LICENSE) {
    window.location.assign(LICENSE);
  }
}

export function convertObjectToQueryParams(
  object: Record<string, string>,
): string {
  if (!isNil(object) && isObject(object)) {
    return "?" + new URLSearchParams(object).toString();
  } else {
    return "";
  }
}

export function getISOFormatDateString(date: Date) {
  return date.toISOString().split("T")[0];
}
